import { toastApiRef } from '@backstage/frontend-plugin-api';
/**
 * https://github.com/backstage/backstage/blob/master/plugins/notifications/src/components/NotificationsSideBarItem/NotificationsSideBarItem.tsx
 */

import { useState, useCallback, useEffect, ComponentType } from 'react';
import { Link, LinkButton } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { useSignal } from '@backstage/plugin-signals-react';
import {
  Notification,
  NotificationSignal,
} from '@backstage/plugin-notifications-common';
import {
  notificationsApiRef,
  useNotificationsApi,
} from '@backstage/plugin-notifications';
import {
  closeSnackbar,
  enqueueSnackbar,
  MaterialDesignContent,
  OptionsWithExtraProps,
  SnackbarKey,
  SnackbarProvider,
  VariantType,
} from 'notistack';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import OpenInNew from '@mui/icons-material/OpenInNew';
import MarkAsReadIcon from '@mui/icons-material/CheckCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Badge from '@mui/material/Badge';

import { useWebNotifications } from '../../hooks/useWebNotifications';
import { useTitleCounter } from '../../hooks/useTitleCounter';
import { SeverityIcon } from './SeverityIcon';
import { notificationsRootPath } from '../../routes';
import classNames from 'classnames';
import { useNotificationsHeaderItemStyles } from './notifications-header-item.styles';

const StyledMaterialDesignContent = styled(MaterialDesignContent)(
  ({ theme }) => ({
    '&.notistack-MuiContent-low': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    '&.notistack-MuiContent-normal': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    '&.notistack-MuiContent-high': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    '&.notistack-MuiContent-critical': {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    '& .MuiLink-underlineHover': {
      color: 'inherit',

      '&:hover': {
        color: 'inherit',
        textDecoration: 'none',
      },
    },
  }),
);

declare module 'notistack' {
  interface VariantOverrides {
    // Custom variants for the snackbar
    low: true;
    normal: true;
    high: true;
    critical: true;
  }
}

/** @public */
export const NotificationsHeaderItem = (props?: {
  webNotificationsEnabled?: boolean;
  titleCounterEnabled?: boolean;
  snackbarEnabled?: boolean;
  snackbarAutoHideDuration?: number | null;
  className?: string;
  Icon?: ComponentType<{ fontSize?: 'inherit' | 'small' | 'medium' | 'large' }>;
  text?: string;
  disableHighlight?: boolean;
  noTrack?: boolean;
}) => {
  const {
    webNotificationsEnabled = false,
    titleCounterEnabled = true,
    snackbarEnabled = true,
    snackbarAutoHideDuration = 10000,
    Icon = NotificationsIcon,
    text = 'Notifications',
    className = '',
    ...restProps
  } = props ?? {
    webNotificationsEnabled: false,
    titleCounterEnabled: true,
    snackbarEnabled: true,
    snackbarAutoHideDuration: 10000,
  };

  const classes = useNotificationsHeaderItemStyles();

  const { loading, error, value, retry } = useNotificationsApi(api =>
    api.getStatus(),
  );
  const notificationsApi = useApi(notificationsApiRef);
  const toastApi = useApi(toastApiRef);
  const [unreadCount, setUnreadCount] = useState(0);
  const notificationsRoute = notificationsRootPath;
  // TODO: Do we want to add long polling in case signals are not available
  const { lastSignal } = useSignal<NotificationSignal>('notifications');
  const { sendWebNotification, requestUserPermission } = useWebNotifications(
    webNotificationsEnabled,
    notificationsRoute,
  );
  const [refresh, setRefresh] = useState(false);
  const { setNotificationCount } = useTitleCounter();

  const getSnackbarProperties = useCallback(
    (notification: Notification) => {
      const action = (snackBarId: SnackbarKey) => (
        <>
          <IconButton
            component={Link}
            to={notification.payload.link ?? notificationsRoute}
            onClick={() => {
              if (notification.payload.link) {
                notificationsApi
                  .updateNotifications({
                    ids: [notification.id],
                    read: true,
                  })
                  .catch(() => {
                    toastApi.post({
                      title: 'Failed to mark notification as read',
                      status: 'danger',
                    });
                  });
              }
              closeSnackbar(snackBarId);
            }}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              notificationsApi
                .updateNotifications({
                  ids: [notification.id],
                  read: true,
                })
                .then(() => {
                  closeSnackbar(snackBarId);
                })
                .catch(() => {
                  toastApi.post({
                    title: 'Failed to mark notification as read',
                    status: 'danger',
                  });
                });
            }}
          >
            <MarkAsReadIcon fontSize="small" />
          </IconButton>
        </>
      );

      return { action };
    },
    [notificationsRoute, notificationsApi, toastApi],
  );

  useEffect(() => {
    if (refresh) {
      retry();
      setRefresh(false);
    }
  }, [refresh, retry]);

  useEffect(() => {
    const handleNotificationSignal = (signal: NotificationSignal) => {
      if (
        (!webNotificationsEnabled && !snackbarEnabled) ||
        signal.action !== 'new_notification'
      ) {
        return;
      }
      notificationsApi
        .getNotification(signal.notification_id)
        .then(notification => {
          if (!notification) {
            return;
          }
          if (webNotificationsEnabled) {
            sendWebNotification({
              id: notification.id,
              title: notification.payload.title,
              description: notification.payload.description ?? '',
              link: notification.payload.link,
            });
          }
          if (snackbarEnabled) {
            const { action } = getSnackbarProperties(notification);
            const snackBarText =
              notification.payload.title.length > 50
                ? `${notification.payload.title.substring(0, 50)}...`
                : notification.payload.title;
            enqueueSnackbar(snackBarText, {
              key: notification.id,
              variant: notification.payload.severity,
              anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
              action,
              autoHideDuration: snackbarAutoHideDuration,
            } as OptionsWithExtraProps<VariantType>);
          }
        })
        .catch(() => {
          toastApi.post({
            title: 'Failed to fetch notification',
            status: 'danger',
          });
        });
    };

    if (lastSignal && lastSignal.action) {
      handleNotificationSignal(lastSignal);
      setRefresh(true);
    }
  }, [
    lastSignal,
    sendWebNotification,
    webNotificationsEnabled,
    snackbarEnabled,
    snackbarAutoHideDuration,
    notificationsApi,
    toastApi,
    getSnackbarProperties,
  ]);

  useEffect(() => {
    if (!loading && !error && value) {
      setUnreadCount(value.unread);
    }
  }, [loading, error, value]);

  useEffect(() => {
    if (titleCounterEnabled) {
      setNotificationCount(unreadCount);
    }
  }, [titleCounterEnabled, unreadCount, setNotificationCount]);

  const count = !error && !!unreadCount ? unreadCount : undefined;

  return (
    <>
      {snackbarEnabled && (
        <SnackbarProvider
          iconVariant={{
            normal: <SeverityIcon severity="normal" />,
            critical: <SeverityIcon severity="critical" />,
            high: <SeverityIcon severity="high" />,
            low: <SeverityIcon severity="low" />,
          }}
          Components={{
            normal: StyledMaterialDesignContent,
            critical: StyledMaterialDesignContent,
            high: StyledMaterialDesignContent,
            low: StyledMaterialDesignContent,
          }}
        />
      )}

      <LinkButton
        aria-label="notifications"
        to={notificationsRoute}
        onClick={() => {
          requestUserPermission();
        }}
        data-testid="notifications-button"
        startIcon={
          Icon && (
            <Badge
              badgeContent={(count ?? 0) > 99 ? '99+' : (count ?? 0)}
              color="error"
              invisible={!count || count <= 0}
              overlap="circular"
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Icon />
            </Badge>
          )
        }
        className={classNames(classes.linkButtons, className)}
        {...restProps}
      />
    </>
  );
};
